import React, { useContext } from "react";
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_DOWNLOAD_APPS,
  PAGE_ROUTE_LOGIN,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import {
  blogHeroImageSx,
  blogSectionImageSx,
  publicInfoBodySx,
  publicInfoLinkSx,
  publicInfoSectionTitleSx,
  publicInfoSectionTitleWithImageSx,
  publicInfoUnorderedListSx
} from "./constants/publicInfoPageStyles";
import { BLOG_CTA } from "./constants/blogContent";
import { BLOG_HERO_IMAGE, BLOG_SECTION_IMAGES } from "./constants/blogImages";
import type {
  BlogImageConfig,
  BlogSection,
  BlogSubsection,
  BlogTable,
  BlogText
} from "./types";

export const LearnMoreParagraphs: React.FC<{ items?: BlogText[] }> = ({
  items
}) => {
  const { t } = useKivunovaTranslation();
  if (!items?.length) {
    return null;
  }

  return (
    <>
      {items.map((item) => (
        <Typography
          key={item.key}
          variant="body1"
          sx={{ ...publicInfoBodySx, mb: 1.5 }}
        >
          {t(item.key, item.fallback)}
        </Typography>
      ))}
    </>
  );
};

export const LearnMoreList: React.FC<{ items?: BlogText[] }> = ({ items }) => {
  const { t } = useKivunovaTranslation();
  if (!items?.length) {
    return null;
  }

  return (
    <Box component="ul" sx={{ ...publicInfoUnorderedListSx, mb: 1.5 }}>
      {items.map((item) => (
        <Box key={item.key} component="li">
          {t(item.key, item.fallback)}
        </Box>
      ))}
    </Box>
  );
};

const LearnMoreTableView: React.FC<{ table?: BlogTable }> = ({ table }) => {
  const { t } = useKivunovaTranslation();
  if (!table) {
    return null;
  }

  return (
    <TableContainer sx={{ mb: 2 }}>
      <Table
        size="small"
        aria-label={t(table.headerKeys[0], table.headerFallbacks[0])}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>
              {t(table.headerKeys[0], table.headerFallbacks[0])}
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>
              {t(table.headerKeys[1], table.headerFallbacks[1])}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {table.rows.map((row) => (
            <TableRow key={row.rowKey}>
              <TableCell>{t(`${row.rowKey}-country`, row.cells[0])}</TableCell>
              <TableCell>{t(`${row.rowKey}-currency`, row.cells[1])}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LearnMoreSubsectionView: React.FC<{ subsection: BlogSubsection }> = ({
  subsection
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography
        component="h3"
        sx={{
          ...publicInfoSectionTitleSx,
          fontSize: { xs: "0.9375rem", sm: "1rem" },
          color: "text.primary"
        }}
      >
        {t(subsection.titleKey, subsection.titleFallback)}
      </Typography>
      <LearnMoreParagraphs items={subsection.paragraphs} />
      <LearnMoreList items={subsection.listItems} />
      <LearnMoreTableView table={subsection.table} />
    </Box>
  );
};

export const LearnMoreSectionView: React.FC<{ section: BlogSection }> = ({
  section
}) => {
  const { t } = useKivunovaTranslation();
  const leadParagraphs =
    section.leadParagraphs ??
    (section.tailParagraphs ? undefined : section.paragraphs);
  const tailParagraphs = section.tailParagraphs;
  const sectionImage = BLOG_SECTION_IMAGES[section.id];

  return (
    <Box component="section" aria-labelledby={`blog-section-${section.id}`}>
      <Typography
        id={`blog-section-${section.id}`}
        component="h2"
        sx={publicInfoSectionTitleWithImageSx}
      >
        {t(section.titleKey, section.titleFallback)}
      </Typography>
      {sectionImage ? (
        <LearnMoreImage
          image={sectionImage}
          variant="section"
          testId={`blog-section-image-${section.id}`}
        />
      ) : null}
      <LearnMoreParagraphs items={leadParagraphs} />
      <LearnMoreList items={section.listItems} />
      <LearnMoreParagraphs items={tailParagraphs} />
      <LearnMoreTableView table={section.table} />
      {section.subsections?.map((subsection) => (
        <LearnMoreSubsectionView key={subsection.id} subsection={subsection} />
      ))}
    </Box>
  );
};

export const LearnMoreImage: React.FC<{
  image: BlogImageConfig;
  variant: "hero" | "section";
  testId: string;
}> = ({ image, variant, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={variant === "hero" ? blogHeroImageSx : blogSectionImageSx}
    />
  );
};

export const LearnMoreHeroImage: React.FC = () => (
  <LearnMoreImage
    image={BLOG_HERO_IMAGE}
    variant="hero"
    testId="blog-hero-image"
  />
);

export const LearnMoreCtaSection: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  return (
    <PublicInfoCtaPanel
      title={t(BLOG_CTA.titleKey, BLOG_CTA.titleFallback)}
      testId="learn-more-page-cta"
    >
      <LearnMoreParagraphs items={BLOG_CTA.paragraphs} />
      <Box component="ul" sx={publicInfoUnorderedListSx}>
        {BLOG_CTA.listItems.map((item, index) => (
          <Box key={item.key} component="li">
            {index === 0 ? (
              <>
                {t(
                  "blog-cta-li-1-prefix",
                  "Create your account and explore available plans—including a "
                )}{" "}
                <MuiLink
                  component={Link}
                  to={pathFor(PAGE_ROUTE_LOGIN)}
                  underline="hover"
                  sx={publicInfoLinkSx}
                >
                  {t("blog-cta-li-1-link", "7-day free trial")}
                </MuiLink>
                {t("blog-cta-li-1-suffix", " where offered")}
              </>
            ) : index === 1 ? (
              <>
                {t("blog-cta-li-2-prefix", "Read the ")}{" "}
                <MuiLink
                  component={Link}
                  to={pathFor(PAGE_ROUTE_USER_GUIDE)}
                  underline="hover"
                  sx={publicInfoLinkSx}
                >
                  {t("blog-cta-li-2-link", "User guide")}
                </MuiLink>
                {t("blog-cta-li-2-suffix", " to get started step by step")}
              </>
            ) : (
              <>
                <MuiLink
                  component={Link}
                  to={pathFor(PAGE_ROUTE_CONTACT)}
                  underline="hover"
                  sx={publicInfoLinkSx}
                >
                  {t("blog-cta-li-3-link", "Contact us")}
                </MuiLink>
                {t(
                  "blog-cta-li-3-suffix",
                  " if you have questions about availability in your country or the right plan for your business"
                )}
              </>
            )}
          </Box>
        ))}
      </Box>
      <Typography variant="body1" sx={{ ...publicInfoBodySx, mt: 2 }}>
        {t("blog-cta-download-prefix", "Prefer an installed app? Visit ")}{" "}
        <MuiLink
          component={Link}
          to={pathFor(PAGE_ROUTE_DOWNLOAD_APPS)}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("blog-cta-download-link", "Download apps")}
        </MuiLink>
        {t("blog-cta-download-suffix", " for Windows and Mac.")}
      </Typography>
      <Typography variant="body1" sx={{ ...publicInfoBodySx, mt: 1.5 }}>
        {t("blog-cta-website-prefix", "Learn more at ")}{" "}
        <MuiLink
          href="https://kivufms.com"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={publicInfoLinkSx}
        >
          kivufms.com
        </MuiLink>
        .
      </Typography>
    </PublicInfoCtaPanel>
  );
};

export const LearnMoreSectionsStack: React.FC<{
  sections: BlogSection[];
}> = ({ sections }) => (
  <Stack spacing={4} sx={{ textAlign: "left" }}>
    {sections.map((section) => (
      <LearnMoreSectionView key={section.id} section={section} />
    ))}
  </Stack>
);
